import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Badge, Modal, Button } from 'react-bootstrap';
import ThreeImageTilt from './components/ThreeImageTiltSimple/ThreeImageTiltSimple';
import ChartSensores from './components/ChartSensores/ChartSensores';
import Filter from './components/Filter/Filter';
import Page from '../../components/Page/Page';
import Headers from '../../components/Headers/Headers';
import ApiGServer from '../../services/ApiGServer';
import Reload from '../../components/reload/reload';
import Api from '../../services/Api';
import { useWebSocket } from '../../hooks/webSocketContext';
import decodeJwt from '../../hooks/decode';
import { track } from '../../analytics';

import './DryerDetail.css';

const DryerDetail = () => {

    const { id } = useParams();
    const socket = useWebSocket();
    const navigate = useNavigate();

    const userDecoded = decodeJwt();
    const { permissionSGU } = userDecoded;
    if (permissionSGU?.dryer?.read !== true) {
        navigate("/home");
    }

    const verdeEscuro = "#1c4220";
    const verdeClaro = "#8fad15";

    const cooperative = useMemo(() => {
        return (
            JSON.parse(localStorage.getItem('units')) ||
            [JSON.parse(localStorage.getItem('cooperative'))] ||
            []
        );
    }, []);

    // Buscar o secador específico pelo ID usando useMemo para otimizar
    const secadorSelecionado = useMemo(() => {
        if (!Array.isArray(cooperative)) return null;

        // Procura dentro de cada cooperative os dryers
        for (const coop of cooperative) {
            const dryer = coop?.dryer?.find(dryer => dryer._id === id);
            if (dryer) return dryer;
        }

        return null;
    }, [cooperative, id]);

    const [equipamentos, setEquipamentos] = useState([]);
    const [equipamentoSelecionado, setEquipamentoSelecionado] = useState(null);
    const [sensores, setSensores] = useState([]);
    const [sensorSelecionado, setSensorSelecionado] = useState(null);
    const [imagemCarregando, setImagemCarregando] = useState(false);
    const [showFullscreenModal, setShowFullscreenModal] = useState(false);
    const [loading, setLoading] = useState(true);

    // Estado para dados do gráfico
    const [dadosGrafico, setDadosGrafico] = useState([]);

    // Estado para tooltip do sensor no modal
    const [tooltipSensor, setTooltipSensor] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    // Estado para armazenar as medições recebidas
    const [medicoes, setMedicoes] = useState([]);
    // Destaque sutil quando uma medida chegar para um equipamento
    const [highlightedSerials, setHighlightedSerials] = useState([]);
    const highlightTimersRef = useRef({});

    // Estados do filtro para histórico
    const [filterData, setFilterData] = useState('all'); // 'all' | 'input' | 'output'
    const [ultimoClicado, setUltimoClicado] = useState(0); // índice do atalho selecionado
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [lastData, setLastData] = useState(null); // quantidade de dias
    const [customFilter, setCustomFilter] = useState({ contrato: '', cnpj: '', lote: '', placa: '' });

    // Usuário atual (para identificar no join do socket)
    const [user] = useState(() => {
        const storedUser = localStorage.getItem('user');
        return storedUser ? JSON.parse(storedUser) : null;
    });

    // Controle de join para evitar múltiplos envios
    const isJoinedRef = useRef(false);

    useEffect(() => {
        if (!secadorSelecionado) return;
        const equipmentId =
            secadorSelecionado.serialnumber ||
            secadorSelecionado.serial ||
            secadorSelecionado.name ||
            id;
        track("dryer_viewed", {
            equipmentId: String(equipmentId),
            dryerId: id,
        });
    }, [id, secadorSelecionado]);


    useEffect(() => {
        let intervalId;

        // Função para buscar equipamentos - definida localmente para evitar dependências
        const buscarEquipamentos = async (equipments) => {
            if (!equipments?.length) {
                setLoading(false);
                return;
            }

            try {
                const filterPayload = { filter: equipments };

                const [equipmentRes, measurementsRes] = await Promise.all([
                    ApiGServer.post('/equipment/filter', filterPayload),
                    Api.post('/measurements/dryer/last', filterPayload),
                ]);

                setEquipamentos(equipmentRes.data || []);
                setMedicoes(measurementsRes.data || []);

            } catch (error) {
                console.error("Erro ao buscar dados:", error);
                setEquipamentos([]);
                setMedicoes([]);
            } finally {
                setLoading(false);
            }
        };

        if (secadorSelecionado?.equipamentos?.length > 0) {
            buscarEquipamentos(secadorSelecionado.equipamentos);

            /*             intervalId = setInterval(() => {
                            buscarEquipamentos(secadorSelecionado.equipamentos);
                        }, 300000); */
        } else {
            console.log("Secador não encontrado ou não possui equipamentos");
            setLoading(false);
        }

        // Limpar o intervalo quando o componente desmontar ou dependências mudarem
        return () => {
            clearInterval(intervalId);
        };

    }, [id, secadorSelecionado]); // Dependências


    // Atualiza sensores ao trocar de equipamento selecionado
    useEffect(() => {
        if (!equipamentoSelecionado || !medicoes.length) {
            setSensores([]);
            setSensorSelecionado(null); // Só limpa quando não há equipamento
            return;
        }
        // Busca a medição pelo serialnumber do equipamento (corrigido)
        const med = medicoes.find(m => m.serialnumber === equipamentoSelecionado.serialnumber);
        if (med && Array.isArray(med.sensores)) {
            // Adiciona id sequencial e status para cada sensor
            const novosSensores = med.sensores.map((s, idx) => ({
                ...s,
                id: idx + 1,
                status: 'normal' // ou defina lógica de alerta se desejar
            }));

            setSensores(novosSensores);

            // Verificar se o sensor atual ainda existe, mas usar callback para evitar dependência
            setSensorSelecionado(sensorAtual => {
                if (sensorAtual) {
                    const sensorExiste = novosSensores.find(s => s.id === sensorAtual.id);
                    return sensorExiste ? sensorAtual : null;
                }
                return null;
            });
        } else {
            setSensores([]);
            setSensorSelecionado(null);
        }
    }, [equipamentoSelecionado, medicoes]);

    // Atualiza dinamicamente os valores do sensorSelecionado sempre que as medições mudam
    useEffect(() => {
        if (!sensorSelecionado || !equipamentoSelecionado || !medicoes.length) return;
        const med = medicoes.find(m => m.serialnumber === equipamentoSelecionado.serialnumber);
        if (med && Array.isArray(med.sensores)) {
            let sensorAtualizado = null;
            if (sensorSelecionado.hasOwnProperty('serial')) {
                sensorAtualizado = med.sensores.find(s => s.serial === sensorSelecionado.serial);
            } else {
                sensorAtualizado = med.sensores[sensorSelecionado.id - 1];
            }
            if (sensorAtualizado) {
                // Só atualiza se algum valor mudou
                const campos = ['ta', 'tg', 'ur', 'status'];
                const mudou = campos.some(c => sensorAtualizado[c] !== sensorSelecionado[c]);
                if (mudou) {
                    setSensorSelecionado({ ...sensorAtualizado, id: sensorSelecionado.id });
                }
            }
        }
    }, [medicoes, equipamentoSelecionado, sensorSelecionado]);

    // Conexão via WebSocket para receber medições em tempo real dos equipamentos deste secador
    useEffect(() => {
        const dryerSerials = Array.isArray(secadorSelecionado?.equipamentos)
            ? secadorSelecionado.equipamentos.filter(Boolean)
            : [];

        if (!socket || !dryerSerials.length || !user?.email) {
            return;
        }

        // Ao mudar a lista de seriais, permitimos novo join
        isJoinedRef.current = false;

        const connectClient = () => {
            if (!isJoinedRef.current && socket.readyState === WebSocket.OPEN) {
                try {
                    socket.send(
                        JSON.stringify({
                            type: 'join',
                            room: dryerSerials,
                            content: { name: `${user.email}-DryerDetail` },
                        })
                    );
                    isJoinedRef.current = true;
                } catch (err) {
                    console.error('Erro ao enviar join via WebSocket:', err);
                }
            }
        };

        const handleOpen = () => connectClient();

        const handleMessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                // Atualiza medições quando receber uma medida de um dos seriais do secador
                const serial = data?.medida?.serialnumber;
                if (serial && serial.includes("PRAR") && dryerSerials.includes(serial)) {

                    setMedicoes((prev) => {
                        const idx = prev.findIndex((m) => m.serialnumber === serial);
                        if (idx !== -1) {
                            const novo = [...prev];
                            // Mescla mantendo outros campos (incluindo sensores)
                            novo[idx] = { ...novo[idx], ...data.medida };
                            return novo;
                        }
                        return [...prev, data.medida];
                    });

                    // Destacar equipamento de forma sutil por alguns segundos
                    setHighlightedSerials((prev) => (prev.includes(serial) ? prev : [...prev, serial]));
                    // Reinicia timer por serial
                    if (highlightTimersRef.current[serial]) {
                        clearTimeout(highlightTimersRef.current[serial]);
                    }
                    highlightTimersRef.current[serial] = setTimeout(() => {
                        setHighlightedSerials((prev) => prev.filter((s) => s !== serial));
                        delete highlightTimersRef.current[serial];
                    }, 5000);
                }
            } catch (error) {
                // Pode haver mensagens de ping/pong ou outros formatos
                // Evitar quebra do fluxo
            }
        };

        if (socket.readyState === WebSocket.OPEN) {
            connectClient();
        }

        // Usar event listeners para não sobrescrever onmessage de outros componentes
        socket.addEventListener('open', handleOpen);
        socket.addEventListener('message', handleMessage);

        return () => {
            try {
                socket.removeEventListener('open', handleOpen);
                socket.removeEventListener('message', handleMessage);
            } catch (e) {
                // noop
            }
            // Limpa timers de destaque ao desmontar
            Object.values(highlightTimersRef.current || {}).forEach((t) => clearTimeout(t));
            highlightTimersRef.current = {};
            setHighlightedSerials([]);
        };
    }, [socket, user, secadorSelecionado?.equipamentos]);

    const handleEquipamentoClick = useCallback(async (equipamento, override = null) => {
        setLoading(true);
        setImagemCarregando(true);
        setEquipamentoSelecionado(equipamento);
        try {
            // Monta os parâmetros do filtro atuais (ou sobrescritos pelo Filter)
            let current = override || {
                type: filterData,
                days: lastData,
                daysClick: ultimoClicado,
                startDate,
                endDate,
                ...customFilter,
            };

            // Padrão: se não houver período selecionado e não for intervalo customizado,
            // aplicar filtro de 1 dia
            const hasCustomDates = Boolean(current.startDate && current.endDate);
            if (!hasCustomDates) {
                const daysDefaulted = (current.days ?? 1);
                current = {
                    ...current,
                    days: daysDefaulted,
                    // opcionalmente inferir o índice do atalho quando 1 dia for aplicado pela primeira vez
                    daysClick: (current.daysClick ?? (daysDefaulted === 1 ? 0 : null)),
                };
            }

            const payload = {
                filter: [equipamento.serialnumber],
                query: current,
            };

            const { data } = await Api.post('/measurements/dryer/history', payload);
            setDadosGrafico(data || []);
        } catch (error) {
            setDadosGrafico([]);
            console.error('Erro ao buscar dados do gráfico:', error);
        } finally {
            setLoading(false);
        }

        setTimeout(() => setImagemCarregando(false), 300);
    }, [filterData, lastData, ultimoClicado, startDate, endDate, customFilter]);

    const handleSensorClick = (sensor) => {
        // Força novo objeto para garantir atualização do React
        setSensorSelecionado({ ...sensor });
    };

    const handleHotspotClick = (equipamento) => {
        handleEquipamentoClick(equipamento);
    };

    // Funções para gerenciar tooltip do sensor
    const handleSensorMouseEnter = (sensor, event) => {
        setTooltipSensor(sensor);
        setTooltipPosition({ x: event.clientX, y: event.clientY });
    };

    const handleSensorMouseLeave = () => {
        setTooltipSensor(null);
    };

    const handleSensorMouseMove = (event) => {
        if (tooltipSensor) {
            setTooltipPosition({ x: event.clientX, y: event.clientY });
        }
    };

    // Função para fechar o modal
    const handleCloseModal = useCallback(() => {
        setShowFullscreenModal(false);
    }, []);

    // Efeito para gerenciar teclas de atalho no modal
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (showFullscreenModal && event.key === 'Escape') {
                setShowFullscreenModal(false); // Usar diretamente o setState
            }
        };

        if (showFullscreenModal) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevenir scroll da página de fundo
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [showFullscreenModal]);

    // Monta o grid de 20 posições, preenchendo com null onde não houver equipamento
    const equipamentosGrid = useMemo(() => {
        return Array.from({ length: 20 }, (_, idx) => {
            // Procura o equipamento com a posição correspondente (position pode ser string ou número)
            return equipamentos.find(eq => Number(eq.position) === idx + 1) || null;
        });
    }, [equipamentos]);

    return (
        <Page title={secadorSelecionado ? `Secador - ${secadorSelecionado.nome}` : "Secador"}>
            <Headers page={secadorSelecionado ? `Secador - ${secadorSelecionado.nome}` : "Secador"} />
            {loading && <Reload />}

            {/* Caso o secador não seja encontrado */}
            {!secadorSelecionado && !loading && (
                <Row className="mb-3">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-0 text-center">
                                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
                                <h4 className="mb-2" style={{ color: verdeEscuro }}>Secador não encontrado</h4>
                                <p className="text-muted mb-0">
                                    O secador com ID "{id}" não foi encontrado nos dados da cooperativa.
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}


            <Row className="content-row g-2">
                <Col lg={4} md={12} className="right-panel">
                    <Card className="equipment-view">
                        <Card.Header style={{ minHeight: 100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h5 className="panel-title" style={{ color: 'white', margin: 0 }}>
                                    🔧 {equipamentoSelecionado ? `Equipamento ${equipamentoSelecionado.serialnumber}` : 'Modelo 3D do Secador'}
                                    {equipamentoSelecionado && (
                                        <Badge
                                            bg={equipamentoSelecionado.status === 'alerta' ? 'warning' : 'success'}
                                            className="ms-3"
                                            style={{
                                                backgroundColor: equipamentoSelecionado.status === 'alerta' ? '#ffb84d' : verdeClaro,
                                                borderColor: equipamentoSelecionado.status === 'alerta' ? '#ffb84d' : verdeClaro
                                            }}
                                        >
                                            {equipamentoSelecionado.status === 'alerta' ? '⚠️ ALERTA' : '✅ NORMAL'}
                                        </Badge>
                                    )}
                                </h5>
                                {!equipamentoSelecionado && (
                                    <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
                                        Clique nos hotspots para selecionar equipamentos
                                    </small>
                                )}
                            </div>
                            {equipamentos.length > 0 && (
                                <Button
                                    variant="outline-light"
                                    size="sm"
                                    onClick={() => setShowFullscreenModal(true)}
                                    title="Clique para visualizar o modelo 3D em tela cheia (ESC para fechar)"
                                    style={{
                                        borderColor: 'rgba(255,255,255,0.3)',
                                        color: 'white',
                                        minWidth: 'auto',
                                        padding: '4px 8px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = 'rgba(255,255,255,0.1)';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.5)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                                    }}
                                >
                                    🔍 Expandir
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-center align-items-center">
                            <ThreeImageTilt
                                alt={equipamentoSelecionado ? `Equipamento ${equipamentoSelecionado.serialnumber}` : "Modelo 3D do Secador"}
                                loading={imagemCarregando}
                                onLoad={() => setImagemCarregando(false)}
                                onError={() => setImagemCarregando(false)}
                                onHotspotClick={handleHotspotClick}
                                sensores={sensores}
                                sensorSelecionado={sensorSelecionado}
                                equipamentos={equipamentos}
                                equipamentoSelecionado={equipamentoSelecionado}
                                medicoes={medicoes}
                            />
                        </Card.Body>
                    </Card>
                </Col>


                <Col lg={4} md={12} className="left-panel">
                    <Card className="equipment-panel">
                        <Card.Header style={{ minHeight: 100, borderRadius: "16px 16px 0px 0px" }}>
                            <h5 className="panel-title" style={{ color: 'white' }}>
                                <Badge bg="primary" className="me-3" style={{ backgroundColor: verdeEscuro }}>📊 {equipamentos.length}</Badge>
                                🏭 Equipamentos Ativos
                            </h5>
                            <div className="equipamentos-legenda">
                                <span className="entrada"><strong>1-10:</strong> Temperatura de <b>Entrada</b></span>
                                <span className="saida"><strong>11-20:</strong> Temperatura de <b>Saída</b></span>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: 5 }}>
                            <div className="equipment-grid">
                                {equipamentosGrid.map((equipamento, idx) => {
                                    const posicao = idx + 1;
                                    const tipo = posicao <= 10 ? 'Entrada' : 'Saída';

                                    if (equipamento) {
                                        return (
                                            <div
                                                key={equipamento._id}
                                                className={`equipment-item ${equipamento.status} ${equipamentoSelecionado?._id === equipamento._id ? 'selected' : ''}`}
                                                onClick={() => handleEquipamentoClick(equipamento)}
                                                title={`Posição: ${equipamento.position} • ${tipo}`}
                                            >
                                                {highlightedSerials.includes(equipamento.serialnumber) && (
                                                    <span className="realtime-indicator" title="Medida recebida agora" />
                                                )}
                                                <div className="equipment-label" style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                    <span>Posição {equipamento.position}</span>
                                                    <small
                                                        className={`equipment-type ${tipo.toLowerCase()}`}
                                                        style={{
                                                            fontSize: 9,
                                                            padding: '2px 6px',
                                                            borderRadius: '999px',
                                                            background: tipo === 'Entrada' ? 'rgba(77, 171, 247, 0.15)' : 'rgba(143, 173, 21, 0.15)',
                                                            color: tipo === 'Entrada' ? '#4dabf7' : '#8fad15',
                                                            border: `1px solid ${tipo === 'Entrada' ? 'rgba(77, 171, 247, 0.4)' : 'rgba(143, 173, 21, 0.4)'}`,
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {tipo}
                                                    </small>
                                                </div>
                                                <div className="equipment-serial" style={{ fontSize: 8, opacity: 0.9 }}>{equipamento.serialnumber}</div>
                                            </div>
                                        );
                                    }

                                    // Item vazio mantendo a indicação de posição e tipo
                                    return (
                                        <div
                                            key={idx}
                                            className="equipment-item empty"
                                            style={{ opacity: 0.3, pointerEvents: 'none' }}
                                            title={`Posição: ${posicao} • ${tipo}`}
                                        >
                                            <div className="equipment-label" style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <span>Posição {posicao}</span>
                                                <small
                                                    className={`equipment-type ${tipo.toLowerCase()}`}
                                                    style={{
                                                        fontSize: 9,
                                                        padding: '2px 6px',
                                                        borderRadius: '999px',
                                                        background: tipo === 'Entrada' ? 'rgba(77, 171, 247, 0.15)' : 'rgba(143, 173, 21, 0.15)',
                                                        color: tipo === 'Entrada' ? '#4dabf7' : '#8fad15',
                                                        border: `1px solid ${tipo === 'Entrada' ? 'rgba(77, 171, 247, 0.4)' : 'rgba(143, 173, 21, 0.4)'}`,
                                                        lineHeight: 1.2
                                                    }}
                                                >
                                                    {tipo}
                                                </small>
                                            </div>
                                            <div className="equipment-serial" style={{ fontSize: 8, opacity: 0.8 }}>—</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Painel Central - Sensores */}
                <Col lg={4} md={12} className="center-panel">
                    <Card className="sensors-panel">
                        <Card.Header style={{ minHeight: 100, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 className="panel-title" style={{ color: 'white' }}>
                                <Badge bg="success" className="me-3" style={{ backgroundColor: verdeClaro }}>🔍 {equipamentoSelecionado ? sensores.length : 0}</Badge>
                                📡 Sensores
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="sensors-list-wrapper">
                                {equipamentoSelecionado ? (
                                    sensores.length > 0 ? (
                                        <div className="sensors-grid">
                                            {[...sensores].reverse()
                                                .filter(sensor => sensor.ta !== -99 && sensor.tg !== -99 && sensor.ur !== -99) // Filtrar sensores que não existem
                                                .map((sensor) => {
                                                    // Verificar se há falha nos sensores (-88)
                                                    const temFalha = sensor.ta === -88 || sensor.tg === -88 || sensor.ur === -88;
                                                    const statusSensor = temFalha ? 'falha' : sensor.status;

                                                    // Definir cor da borda baseada na temperatura ambiente (TA)
                                                    // Escala de cores conforme ThreeImageTiltSimple.js:
                                                    // Verde (#69db7c) - Normal (40°C <= temp <= 80°C)
                                                    // Azul (#4dabf7) - Baixa (temp < 40°C)  
                                                    // Amarelo (#ffd43b) - Média (60°C < temp <= 80°C)
                                                    // Vermelho (#ff6b6b) - Alta (temp > 80°C)
                                                    // Vermelho escuro (#dc3545) - Falha de sensor
                                                    let borderColor = '#69db7c'; // Verde (normal)
                                                    if (!temFalha && sensor.ta !== null && sensor.ta !== undefined) {
                                                        const temp = sensor.ta;
                                                        if (temp > 90) borderColor = '#ff6b6b'; // Vermelho (alta)
                                                        else if (temp > 60) borderColor = '#ffd43b'; // Amarelo (média)
                                                        else if (temp < 40) borderColor = '#4dabf7'; // Azul (baixa)
                                                    } else if (temFalha) {
                                                        borderColor = '#dc3545'; // Vermelho para falha
                                                    }

                                                    return (
                                                        <div
                                                            key={sensor.id}
                                                            className={`sensor-item ${statusSensor} ${sensorSelecionado?.id === sensor.id ? 'selected' : ''}`}
                                                            onClick={() => handleSensorClick(sensor)}
                                                            style={{
                                                                border: `3px solid ${borderColor}`,
                                                                borderRadius: '8px',
                                                                boxShadow: `0 2px 8px ${borderColor}40`,
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                        >
                                                            <div className="sensor-header">
                                                                <span className="sensor-id">
                                                                    S{sensor.id} |
                                                                    {temFalha ? (
                                                                        <span style={{ color: '#dc3545', fontWeight: 'bold' }}> ⚠️ FALHA DETECTADA</span>
                                                                    ) : (
                                                                        <span>🌡️{sensor.ta}°C | 🌾{sensor.tg}°C | 💧{sensor.ur}%</span>
                                                                    )}
                                                                </span>
                                                                <span className={`sensor-status ${statusSensor}`}>
                                                                    {temFalha ? '🔴' : sensor.status === 'alerta' ? '🔴' : '🟢'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '200px',
                                            color: '#6c757d',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                                            <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                                Carregando dados dos sensores...
                                            </p>
                                        </div>
                                    )
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '200px',
                                        color: '#6c757d',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📡</div>
                                        <p style={{ margin: 0, fontSize: '0.9rem' }}>
                                            Selecione um equipamento<br />
                                            para visualizar os sensores
                                        </p>
                                    </div>
                                )}
                            </div>
                            {equipamentoSelecionado && sensores.length > 0 && (
                                <div className="mt-3 pt-2 border-top">
                                    <h6 className="text-center mb-2" style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 'bold' }}>Média dos Sensores</h6>
                                    <div className="d-flex justify-content-around align-items-center text-center">
                                        <div className="average-item">
                                            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Temp. Amb.</small>
                                            <span style={{ fontWeight: 'bold', color: '#4dabf7', fontSize: '1.1rem' }}>
                                                {(() => {
                                                    const validos = sensores.filter(s => s.ta !== -99 && s.ta !== -88 && s.ta != null);
                                                    if (!validos.length) return '-';
                                                    const media = validos.reduce((acc, curr) => acc + Number(curr.ta), 0) / validos.length;
                                                    return media.toFixed(1);
                                                })()}°C
                                            </span>
                                        </div>
                                        <div className="average-item">
                                            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Temp. Grão</small>
                                            <span style={{ fontWeight: 'bold', color: '#ffd43b', fontSize: '1.1rem' }}>
                                                {(() => {
                                                    const validos = sensores.filter(s => s.tg !== -99 && s.tg !== -88 && s.tg != null);
                                                    if (!validos.length) return '-';
                                                    const media = validos.reduce((acc, curr) => acc + Number(curr.tg), 0) / validos.length;
                                                    return media.toFixed(1);
                                                })()}°C
                                            </span>
                                        </div>
                                        <div className="average-item">
                                            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>Umidade</small>
                                            <span style={{ fontWeight: 'bold', color: '#69db7c', fontSize: '1.1rem' }}>
                                                {(() => {
                                                    const validos = sensores.filter(s => s.ur !== -99 && s.ur !== -88 && s.ur != null);
                                                    if (!validos.length) return '-';
                                                    const media = validos.reduce((acc, curr) => acc + Number(curr.ur), 0) / validos.length;
                                                    return media.toFixed(1);
                                                })()}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>




            </Row>

            {/* Detalhes do Sensor (painel global, fora da lista) */}
            {/*             {sensorSelecionado && (
                <Row className="details-row mt-2">
                    <Col>
                        <Card className="sensor-details">
                            <Card.Header>
                                <h5 className="panel-title" style={{ color: 'white' }}>
                                    Detalhes do Sensor {sensorSelecionado.id}
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {(() => {
                                    // Verificar se há falha nos sensores (-88)
                                    const temFalha = sensorSelecionado.ta === -88 || sensorSelecionado.tg === -88 || sensorSelecionado.ur === -88;

                                    if (temFalha) {
                                        return (
                                            <Row>
                                                <Col md={12} className="text-center">
                                                    <div className="detail-item">
                                                        <h4 style={{ color: '#dc3545', marginBottom: '20px' }}>
                                                            ⚠️ FALHA DETECTADA NO SENSOR
                                                        </h4>
                                                        <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>
                                                            Este sensor apresenta problemas de comunicação ou leitura.
                                                            <br />
                                                            Entre em contato com o suporte técnico.
                                                        </p>
                                                        <Badge
                                                            bg="danger"
                                                            className="status-badge mt-3"
                                                            style={{
                                                                backgroundColor: '#dc3545',
                                                                borderColor: '#dc3545',
                                                                fontSize: '1rem',
                                                                padding: '10px 20px'
                                                            }}
                                                        >
                                                            🚨 SENSOR COM FALHA
                                                        </Badge>
                                                    </div>
                                                </Col>
                                            </Row>
                                        );
                                    }

                                    return (
                                        <Row>
                                            <Col md={4} className="text-center">
                                                <div className="detail-item">
                                                    <h6>🌡️ Temperatura Ambiente</h6>
                                                    <div className="detail-value">{sensorSelecionado.ta}°C</div>
                                                </div>
                                            </Col>
                                            <Col md={4} className="text-center">
                                                <div className="detail-item">
                                                    <h6>🌾 Temperatura Grão</h6>
                                                    <div className="detail-value">{sensorSelecionado.tg}°C</div>
                                                </div>
                                            </Col>
                                            <Col md={4} className="text-center">
                                                <div className="detail-item">
                                                    <h6>💧 Umidade Relativa</h6>
                                                    <div className="detail-value">{sensorSelecionado.ur}%</div>
                                                </div>
                                            </Col>
                                            <Col md={12} className="text-center">
                                                <div className="detail-item">
                                                    <h6>📈 Status</h6>
                                                    <Badge
                                                        bg={sensorSelecionado.status === 'alerta' ? 'danger' : 'success'}
                                                        className="status-badge"
                                                        style={{
                                                            backgroundColor: sensorSelecionado.status === 'alerta' ? '#dc3545' : verdeClaro,
                                                            borderColor: sensorSelecionado.status === 'alerta' ? '#dc3545' : verdeClaro
                                                        }}
                                                    >
                                                        {sensorSelecionado.status === 'alerta' ? '🚨 CRÍTICO' : '✅ OPERACIONAL'}
                                                    </Badge>
                                                </div>
                                            </Col>
                                        </Row>
                                    );
                                })()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )} */}

            {/* Gráfico de histórico do equipamento selecionado */}
            {dadosGrafico && dadosGrafico.length > 0 && (
                <>
                    <Filter
                        user={user}
                        allData={null}
                        handleGetPeriod={(days, ultimo, type, custom) => {
                            setLastData(days ?? null);
                            setUltimoClicado(typeof ultimo === 'number' ? ultimo : null);
                            if (type) setFilterData(type);
                            if (custom) setCustomFilter(custom);

                            // Sempre que clicar em um filtro, efetua a busca.
                            const alvo = equipamentoSelecionado || equipamentos.find(Boolean) || null;
                            if (alvo) {
                                const override = {
                                    type: type ?? filterData,
                                    days: days ?? lastData,
                                    daysClick: typeof ultimo === 'number' ? ultimo : ultimoClicado,
                                    startDate,
                                    endDate,
                                    ...(custom || customFilter),
                                };
                                handleEquipamentoClick(alvo, override);
                            }
                        }}
                        setStartDate={(value) => setStartDate(value)}
                        setEndDate={(value) => setEndDate(value)}
                        ultimoClicado={ultimoClicado}
                        filterData={filterData}
                        lastData={lastData}
                        startDate={startDate}
                        endDate={endDate}
                        handledata={() => {
                            const alvo = equipamentoSelecionado || equipamentos.find(Boolean) || null;
                            if (alvo) {
                                const override = {
                                    type: filterData,
                                    days: lastData,
                                    daysClick: ultimoClicado,
                                    startDate,
                                    endDate,
                                    ...customFilter,
                                };
                                handleEquipamentoClick(alvo, override);
                            }
                        }}
                        permissionSGU={permissionSGU}
                    />

                    <Row className="mt-2">
                        <Col>
                            <Card>
                                <Card.Header>
                                    <h5 className="panel-title" style={{ color: "white" }}>
                                        Histórico do Equipamento
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <ChartSensores key={equipamentoSelecionado?.serialnumber} dados={dadosGrafico} />
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Modal para visualização em tela cheia do modelo 3D */}
            <Modal
                show={showFullscreenModal}
                onHide={handleCloseModal}
                size="xl"
                fullscreen
                backdrop="static"
                keyboard={true}
                className="fullscreen-3d-modal"
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: verdeEscuro,
                        borderBottom: `2px solid ${verdeClaro}`,
                        color: 'white'
                    }}
                >
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            🔧 Visualização 3D - {equipamentoSelecionado ? `Equipamento ${equipamentoSelecionado.serialnumber}` : 'Modelo do Secador'}
                            {equipamentoSelecionado && (
                                <Badge
                                    bg={equipamentoSelecionado.status === 'alerta' ? 'warning' : 'success'}
                                    style={{
                                        backgroundColor: equipamentoSelecionado.status === 'alerta' ? '#ffb84d' : verdeClaro,
                                        borderColor: equipamentoSelecionado.status === 'alerta' ? '#ffb84d' : verdeClaro
                                    }}
                                >
                                    {equipamentoSelecionado.status === 'alerta' ? '⚠️ ALERTA' : '✅ NORMAL'}
                                </Badge>
                            )}
                        </div>
                        <small style={{ fontSize: '12px', opacity: 0.8, fontWeight: 'normal' }}>
                            💡 Arraste para rotacionar • Scroll para zoom • ESC para fechar • Clique nos hotspots para selecionar
                        </small>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        padding: 0,
                        height: 'calc(100vh - 60px)',
                        backgroundColor: '#f8f9fa',
                        position: 'relative'
                    }}
                    onMouseMove={handleSensorMouseMove}
                >
                    {/* Tooltip do sensor */}
                    {tooltipSensor && (
                        <div
                            style={{
                                position: 'fixed',
                                left: tooltipPosition.x + 10,
                                top: tooltipPosition.y - 10,
                                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                color: 'white',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                zIndex: 9999,
                                pointerEvents: 'none',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                minWidth: '200px'
                            }}
                        >
                            {(() => {
                                const temFalha = tooltipSensor.ta === -88 || tooltipSensor.tg === -88 || tooltipSensor.ur === -88;

                                if (temFalha) {
                                    return (
                                        <div>
                                            <strong style={{ color: '#ff6b6b' }}>⚠️ Sensor {tooltipSensor.id} - FALHA DETECTADA</strong>
                                            <br />
                                            <small style={{ color: '#ffcc99' }}>
                                                Problemas de comunicação ou leitura
                                            </small>
                                        </div>
                                    );
                                }

                                return (
                                    <div>
                                        <strong>📡 Sensor {tooltipSensor.id}</strong>
                                        <br />
                                        <span>🌡️ Temp. Ambiente: {tooltipSensor.ta}°C</span>
                                        <br />
                                        <span>🌾 Temp. Grão: {tooltipSensor.tg}°C</span>
                                        <br />
                                        <span>💧 Umidade: {tooltipSensor.ur}%</span>
                                        <br />
                                        <span style={{ color: tooltipSensor.status === 'alerta' ? '#ff6b6b' : '#69db7c' }}>
                                            {tooltipSensor.status === 'alerta' ? '🚨 CRÍTICO' : '✅ OPERACIONAL'}
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {equipamentos.length > 0 && (
                        <div style={{ width: '100%', height: '100%' }}>
                            {imagemCarregando && (
                                <div className="loading-overlay">
                                    <div style={{ textAlign: 'center', color: '#1c4220' }}>
                                        <div className="loading-spinner"></div>
                                        <div style={{ marginTop: '15px', fontSize: '16px' }}>
                                            Carregando modelo 3D...
                                        </div>
                                    </div>
                                </div>
                            )}
                            <ThreeImageTilt
                                alt={equipamentoSelecionado ? `Equipamento ${equipamentoSelecionado.serialnumber} - Visualização em Tela Cheia` : "Modelo 3D do Secador - Visualização em Tela Cheia"}
                                loading={imagemCarregando}
                                onLoad={() => setImagemCarregando(false)}
                                onError={() => setImagemCarregando(false)}
                                onHotspotClick={handleHotspotClick}
                                sensores={sensores}
                                sensorSelecionado={sensorSelecionado}
                                equipamentos={equipamentos}
                                equipamentoSelecionado={equipamentoSelecionado}
                                isFullscreen={true}
                                medicoes={medicoes}
                                onSensorMouseEnter={handleSensorMouseEnter}
                                onSensorMouseLeave={handleSensorMouseLeave}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </Page>
    );
};

export default DryerDetail;
